/**
 * Sincronizzazione offline: scarica in background i dati chiave del ruolo così che
 * OGNI schermata sia consultabile senza rete. Le risposte finiscono nella cache del
 * service worker (network-first con fallback cache), quindi le stesse chiamate fatte
 * dalle pagine funzionano anche offline.
 */
import { api } from "@/api/client";
import { markSync } from "@/lib/lastSync";

const iso = (d) => d.toISOString().slice(0, 10);
const shift = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return iso(d);
};

function monthRange() {
  const n = new Date();
  const first = new Date(n.getFullYear(), n.getMonth(), 1);
  const last = new Date(n.getFullYear(), n.getMonth() + 2, 0);
  return { from: iso(first), to: iso(last) };
}

function urlsForRole(role) {
  const today = iso(new Date());
  const week = [0, 1, 2, 3, 4, 5, 6, 7].map(shift);
  const { from, to } = monthRange();
  const n = new Date();
  const month = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  const monthFirst = iso(new Date(n.getFullYear(), n.getMonth(), 1));
  const monthLast = iso(new Date(n.getFullYear(), n.getMonth() + 1, 0));
  const common = ["/auth/me", "/notifications"];

  if (role === "cliente") {
    return [
      ...common,
      "/clients/me/structures",
      "/clients/me/requests",
      "/clients/me/cleanings",
      "/clients/me/delivery-requests",
      "/clients/me/products",
      "/clients/me/last-cleaning",
    ];
  }

  const catalog = ["/products", "/kits", "/structures", "/clients"];

  if (role === "resp_logistica" || role === "rider") {
    return [
      ...common,
      ...catalog,
      ...week.slice(0, 4).map((d) => `/logistics/deliveries?date=${d}`),
      "/logistics/assignable-users",
      "/linen/pending",
      "/hq/stock",
    ];
  }

  const employee = [
    ...week.map((d) => `/cleanings?date=${d}`),
    `/cleanings?date_from=${today}&date_to=${shift(60)}`,
    "/reports",
    "/stats/employee",
    `/daily-tasks?date=${today}`,
  ];

  if (role === "dipendente") return [...common, ...catalog, ...employee];

  // super_admin + responsabile: tutto il gestionale
  const manager = [
    ...common,
    ...catalog,
    ...employee,
    "/stats/admin",
    "/users",
    "/users/pins",
    `/cleanings?date_from=${from}&date_to=${to}`,
    ...week.slice(0, 4).map((d) => `/schedules/${d}`),
    ...week.slice(0, 4).map((d) => `/logistics/deliveries?date=${d}`),
    "/logistics/assignable-users",
    "/hq/stock",
    "/hq/movements",
    "/warehouse/stock",
    "/inventory/overview",
    "/returns",
    "/returns/summary",
    "/returns/checks",
    "/economics/missing-rates",
    "/economics/uncovered-structures",
    "/supplies",
    "/supplies/status",
    `/billing?month=${month}`,
    `/economics/monthly?month=${month}`,
    `/economics/summary?date_from=${monthFirst}&date_to=${monthLast}`,
    `/economics/alerts?date_from=${monthFirst}&date_to=${monthLast}`,
    `/economics/quick-jobs?date_from=${monthFirst}&date_to=${monthLast}`,
    `/laundry/private/totals?date_from=${monthFirst}&date_to=${monthLast}`,
    `/day-history?date=${today}`,
    "/warehouse/movements?limit=60",
    "/city-tax",
    "/city-tax/summary",
    "/laundry/private",
    "/linen/pending",
    "/deviations",
    "/movements",
  ];
  return manager;
}

async function runPool(tasks, size = 4) {
  let i = 0;
  const workers = Array.from({ length: size }, async () => {
    while (i < tasks.length) {
      const t = tasks[i++];
      try { await t(); } catch { /* endpoint non disponibile per questo ruolo */ }
    }
  });
  await Promise.all(workers);
}

let running = false;

/** Scarica tutto il necessario per il ruolo. Silenzioso: non blocca mai la UI. */
export async function syncOffline(role) {
  if (running || !navigator.onLine || !role) return false;
  running = true;
  try {
    const urls = urlsForRole(role);
    let ok = 0;
    await runPool(urls.map((u) => async () => {
      await api.get(u);
      ok += 1;
    }));

    // Dettagli delle pulizie dei prossimi giorni: servono offline sul telefono.
    try {
      const { data } = await api.get(`/cleanings?date_from=${iso(new Date())}&date_to=${shift(7)}`);
      const ids = (Array.isArray(data) ? data : []).slice(0, 60).map((c) => c.id);
      await runPool(ids.map((id) => async () => { await api.get(`/cleanings/${id}`); }));
    } catch { /* ruolo senza accesso alle pulizie */ }

    if (ok > 0) markSync();
    return true;
  } finally {
    running = false;
  }
}
